import {
  withDelay,
  withTiming,
  withSequence,
  withRepeat,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { HapticFeedback } from '@bonanza-social/toolkit/utils';
import {
  PrizeWallPrize,
  PrizeWallPrizeId,
  PrizeWallPrizes,
} from '../../state/prizeWall/prizeWall.types';
import {
  MISS_POSITION,
  ANIMATION_TILES_COUNT,
  ANIMATION_TILE_DURATION,
} from './PrizeWallPrizes.content';

export const groupPrizesByRows = (
  prizes: PrizeWallPrizes | undefined,
  itemsPerRow: number,
) => {
  if (!prizes) {
    return [];
  }

  return prizes.ids.reduce<PrizeWallPrize[][]>(
    (accumulator, prizeId, index) => {
      if (index % itemsPerRow === 0) {
        accumulator.push([]);
      }

      accumulator[accumulator.length - 1].push(prizes.byId[prizeId]);

      return accumulator;
    },
    [],
  );
};

export const getAvailablePositions = (prizes?: PrizeWallPrizes) => {
  if (!prizes) {
    return [];
  }

  return prizes.ids.reduce((accumulator: number[], prizeId, index) => {
    if (prizes.byId[prizeId].remainingQuantity > 0) {
      accumulator.push(index);
    }

    return accumulator;
  }, []);
};

export const shufflePositions = (availablePositions: number[]) => {
  let shuffledPositions = [...availablePositions, MISS_POSITION].sort(() =>
    Math.random() > 0.5 ? -1 : 1,
  );

  while (shuffledPositions.length < ANIMATION_TILES_COUNT) {
    const diff = ANIMATION_TILES_COUNT - shuffledPositions.length;

    shuffledPositions = [
      ...shuffledPositions,
      ...shuffledPositions.slice(0, diff),
    ];
  }

  return shuffledPositions;
};

export const findPosition = (
  prizes: PrizeWallPrizes | undefined,
  rewardId: PrizeWallPrizeId,
) =>
  prizes?.ids.findIndex(
    (prizeId) => prizes.byId[prizeId].rewardId === rewardId,
  );

export const fadeIn = (
  position: number,
  callback?: (isFinished?: boolean) => void,
) =>
  withTiming(
    position + 0.5,
    { duration: ANIMATION_TILE_DURATION / 2 },
    (isFinished) => {
      if (callback) {
        scheduleOnRN<Array<boolean | undefined>, void>(callback, isFinished);
      }
    },
  );

export const fadeOut = (
  position: number,
  callback?: (isFinished?: boolean) => void,
) =>
  withDelay(
    200,
    withTiming(
      position + 1,
      {
        duration: ANIMATION_TILE_DURATION / 2,
      },
      (isFinished) => {
        if (callback) {
          scheduleOnRN<Array<boolean | undefined>, void>(callback, isFinished);
        }
      },
    ),
  );

export const moveInstantly = (position: number) =>
  withTiming(position, { duration: 0 });

const getRewardAnimation = (
  position: number,
  callback?: (isFinished?: boolean) => void,
) =>
  withSequence(
    withTiming(position, { duration: 0 }),
    fadeIn(position, callback),
    fadeOut(position),
  );

export const runMainAnimation = (
  shuffledPositions: number[],
  callback: (position: number, index: number) => void,
) =>
  withRepeat(
    withSequence(
      ...shuffledPositions.map((position, index) =>
        getRewardAnimation(position, (isFinished) => {
          if (isFinished) {
            HapticFeedback.trigger('impactMedium');

            callback(position, index);
          }
        }),
      ),
    ),
    -1,
  );
